import { usePagination } from '@mantine/hooks';
import { BaseRecord, GetListResponse, HttpError } from '@refinedev/core';
import { QueryKey, UseQueryOptions } from '@tanstack/react-query';
import { CrudFilter } from '@refinedev/core';
import { DataTableColumn, DataTableSortStatus } from 'mantine-datatable';
import { useClientPaginatedTable } from './useClientPaginatedTable';
import { useServerPaginatedTable } from './useServerPaginatedTable';

export type TableMode = 'client' | 'server';
export type PaginationData = ReturnType<typeof usePagination>;
type QueryOptions<T> = UseQueryOptions<GetListResponse<T>, HttpError, GetListResponse<T>, QueryKey>;

type UseTableOptions<T> = {
  resource: string;
  mode?: TableMode;
  columns?: DataTableColumn<T>[];
  searchTerm?: string;
  filterPredicate?: (item: T, search: string) => boolean;
  filters?: CrudFilter[];
  pageSize?: number;
  queryOptions?: QueryOptions<T>;
};

export type TableData<T> = {
  data: T[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  pagination: PaginationData;
  totalRecords: number;
  refetch: () => void;
  sortStatus?: DataTableSortStatus<T>;
  onSortStatusChange?: (status: DataTableSortStatus<T>) => void;
};

export const useTable = <T extends BaseRecord>(options: UseTableOptions<T>): TableData<T> => {
  const mode = options.mode || 'client';
  const pageSize = options.pageSize || 10;

  if (mode === 'server') {
    return useServerPaginatedTable<T>(options.resource, pageSize);
  }

  return useClientPaginatedTable<T>(
    options.resource,
    options.columns || [],
    options.searchTerm || '',
    options.filterPredicate,
    options.filters,
    pageSize,
  );
};
